class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';
    this.requestSequence = 0;
    this.setupEventListeners();
    this.setupBarcodeScanner();
  }

  setupBarcodeScanner() {
    const field = this.input?.closest('.field');
    if (!field || field.querySelector('.barcode-scan__button')) return;

    PlanetLineBarcodeScanner.installStyles();
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'barcode-scan__button field__button';
    button.setAttribute('aria-label', 'Scanner un code-barres');
    button.setAttribute('title', 'Scanner un code-barres');
    button.innerHTML = `
      <svg viewBox="0 0 32 32" aria-hidden="true" focusable="false">
        <path d="M5 10V6a1 1 0 0 1 1-1h4M22 5h4a1 1 0 0 1 1 1v4M27 22v4a1 1 0 0 1-1 1h-4M10 27H6a1 1 0 0 1-1-1v-4M9 11v10M12 11v10M16 11v10M19 11v10M23 11v10"/>
      </svg>`;
    button.addEventListener('click', event => {
      event.preventDefault();
      event.stopPropagation();
      PlanetLineBarcodeScanner.open(this.input);
    });

    const resetButton = field.querySelector('.reset__button');
    field.insertBefore(button, resetButton || null);
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));
    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  getQuery() { return this.input.value.trim(); }

  normalizeSku(value) {
    return value.toUpperCase().replace(/[^A-Z0-9]/g, '');
  }

  normalizeText(value) {
    return value
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, ' ')
      .trim();
  }

  isBarcodeLikeQuery(value) {
    return /^\d{8,14}$/.test(value.trim());
  }

  isSkuLikeQuery(value) {
    const query = value.trim();
    const compact = this.normalizeSku(query);
    if (compact.length < 4 || compact.length > 32 || !/\d/.test(compact)) return false;
    if (!/^[A-Za-z0-9][A-Za-z0-9\s._\/-]*$/.test(query)) return false;

    if (/\s/.test(query)) {
      const parts = query.split(/\s+/).filter(Boolean);
      if (parts.length !== 2) return false;
      const alphaNumericReference = /^[A-Za-z]{1,5}$/.test(parts[0]) && /^\d+$/.test(parts[1]);
      const numericReference = /^\d+$/.test(parts[0]) && /^\d+$/.test(parts[1]);
      return alphaNumericReference || numericReference;
    }

    return true;
  }

  getSkuQueryVariants(value) {
    const query = value.trim();
    if (!this.isSkuLikeQuery(query)) return [query];

    // EAN prefixes should not generate arbitrary hyphenated SKU permutations.
    if (/^\d{8,14}$/.test(query)) return [query];
    const variants = [query];
    const compact = this.normalizeSku(query);
    variants.push(compact);

    const alphaNumeric = compact.match(/^([A-Z]{1,5})(\d+)$/);
    if (alphaNumeric) {
      variants.push(`${alphaNumeric[1]}-${alphaNumeric[2]}`);
      variants.push(`${alphaNumeric[1]} ${alphaNumeric[2]}`);
    }

    if (/^\d{5,12}$/.test(compact)) {
      for (let position = 2; position <= compact.length - 2; position += 1) {
        variants.push(`${compact.slice(0, position)}-${compact.slice(position)}`);
      }
    }

    return [...new Set(variants.map(item => item.trim()).filter(Boolean))].slice(0, 10);
  }

  onChange() {
    super.onChange();
    const nextTerm = this.getQuery();
    if (!this.searchTerm || !nextTerm.startsWith(this.searchTerm)) {
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
    }
    this.searchTerm = nextTerm;
    if (!nextTerm.length) {
      this.close(true);
      return;
    }
    this.getSearchResults(nextTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onFormReset() {
    this.searchTerm = '';
    this.abortController.abort();
    this.abortController = new AbortController();
    this.closeResults(true);
  }

  onFocus() {
    const query = this.getQuery();
    if (!query.length) return;
    if (this.searchTerm !== query) this.onChange();
    else if (this.getAttribute('results') === 'true') this.open();
    else this.getSearchResults(query);
  }

  onFocusOut() { setTimeout(() => { if (!this.contains(document.activeElement)) this.close(); }); }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();
    if (event.code === 'ArrowUp') this.switchOption('up');
    if (event.code === 'ArrowDown') this.switchOption('down');
    if (event.code === 'Enter') this.selectOption();
  }

  onKeydown(event) {
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') event.preventDefault();
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;
    const up = direction === 'up';
    const selected = this.querySelector('[aria-selected="true"]');
    const elements = Array.from(this.querySelectorAll('li, button.predictive-search__item')).filter(item => item.offsetParent !== null);
    if (up && !selected) return;
    const index = selected ? elements.indexOf(selected) : -1;
    const next = up ? (index <= 0 ? elements.length - 1 : index - 1) : (index === elements.length - 1 ? 0 : index + 1);
    selected?.setAttribute('aria-selected', false);
    elements[next]?.setAttribute('aria-selected', true);
  }

  selectOption() { this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]')?.click(); }

  buildSearchUrl(term) {
    return `${routes.predictive_search_url}?q=${encodeURIComponent(term)}&${encodeURIComponent('resources[options][fields]')}=variants.sku,variants.barcode,title,product_type,tag&${encodeURIComponent('resources[limit]')}=10&section_id=predictive-search`;
  }

  extractMarkup(text) {
    return new DOMParser().parseFromString(text, 'text/html').querySelector('#shopify-section-predictive-search')?.innerHTML || '';
  }

  mergeSearchResults(markups) {
    const documents = markups.map(markup => new DOMParser().parseFromString(markup, 'text/html'));
    const primaryDocument = documents[0];
    const primaryWithProducts = documents.find(document => document.querySelector('#predictive-search-results-products-list'));
    const baseDocument = primaryDocument.querySelector('#predictive-search-results-products-list') ? primaryDocument : primaryWithProducts || primaryDocument;
    const list = baseDocument.querySelector('#predictive-search-results-products-list');
    if (!list) return baseDocument.body.innerHTML;

    if (baseDocument !== primaryDocument) {
      const originalSearchLabel = primaryDocument.querySelector('[data-predictive-search-search-for-text]')?.innerHTML;
      const mergedSearchLabel = baseDocument.querySelector('[data-predictive-search-search-for-text]');
      if (originalSearchLabel && mergedSearchLabel) mergedSearchLabel.innerHTML = originalSearchLabel;
    }

    const seen = new Set();
    Array.from(list.querySelectorAll('.predictive-search__list-item')).forEach(item => {
      const key = (item.querySelector('a')?.getAttribute('href') || '').split('?')[0] || item.dataset.productSkus || '';
      if (key) seen.add(key);
    });

    for (const document of documents) {
      for (const item of document.querySelectorAll('#predictive-search-results-products-list .predictive-search__list-item')) {
        const key = (item.querySelector('a')?.getAttribute('href') || '').split('?')[0] || item.dataset.productSkus || '';
        if (!key || seen.has(key)) continue;
        seen.add(key);
        list.appendChild(item.cloneNode(true));
      }
    }

    return baseDocument.body.innerHTML;
  }

  getSearchResults(term) {
    const key = term.toLowerCase();
    this.setLiveRegionLoadingState();

    // Every instance owns its requests and cache. Cancel outdated mobile input.
    this.abortController.abort();
    this.abortController = new AbortController();
    const signal = this.abortController.signal;
    const requestId = ++this.requestSequence;

    if (Object.prototype.hasOwnProperty.call(this.cachedResults, key)) {
      this.renderSearchResults(this.cachedResults[key]);
      return;
    }

    const variants = this.getSkuQueryVariants(term);
    if (/^\d{13,14}$/.test(term)) variants.push(term.slice(0, 12));
    if (/^\d{10,14}$/.test(term)) variants.push(term.slice(0, 9));
    if (/^\d{9,14}$/.test(term)) variants.push(term.slice(0, 8));
    const queries = [...new Set(variants)];

    Promise.allSettled(queries.map(query => fetch(this.buildSearchUrl(query), { signal })
      .then(response => {
        if (!response.ok) throw new Error('Predictive search HTTP ' + response.status);
        return response.text();
      })
      .then(text => this.extractMarkup(text))))
      .then(results => {
        if (signal.aborted || requestId !== this.requestSequence || this.getQuery().toLowerCase() !== key) return;
        const markups = results.filter(result => result.status === 'fulfilled' && result.value)
          .map(result => result.value);
        if (!markups.length) {
          this.removeAttribute('loading');
          this.close();
          return;
        }
        const markup = markups.length === 1 ? markups[0] : this.mergeSearchResults(markups);
        // Cache only successful complete response batches.
        if (results.every(result => result.status === 'fulfilled')) this.cachedResults[key] = markup;
        this.renderSearchResults(markup);
      })
      .catch(() => {
        if (signal.aborted || requestId !== this.requestSequence) return;
        this.removeAttribute('loading');
        this.close();
      });
  }

  prioritizeSearchMatches() {
    const rawQuery = this.getQuery().trim().toLowerCase();
    const normalizedQuery = this.normalizeSku(rawQuery);
    const textQuery = this.normalizeText(rawQuery);
    const queryTokens = textQuery.split(' ').filter(Boolean);
    if (!rawQuery) return;
    const list = this.predictiveSearchResults.querySelector('#predictive-search-results-products-list');
    if (!list) return;

    const items = Array.from(list.querySelectorAll('.predictive-search__list-item'));
    const ranked = items.map((item, index) => {
      const skus = (item.dataset.productSkus || '').split('|').map(sku => sku.trim()).filter(Boolean);
      const lowerSkus = skus.map(sku => sku.toLowerCase());
      const normalizedSkus = skus.map(sku => this.normalizeSku(sku));
      const barcodes = (item.dataset.productBarcodes || '').split('|').map(barcode => barcode.trim()).filter(Boolean);
      const title = this.normalizeText(item.dataset.productTitle || item.querySelector('.predictive-search__item-heading')?.textContent || '');
      const productType = this.normalizeText(item.dataset.productType || '');
      const tags = (item.dataset.productTags || '').split('|').map(tag => this.normalizeText(tag)).filter(Boolean);
      let rank = 10;
      if (lowerSkus.includes(rawQuery)) rank = 0;
      else if (this.isBarcodeLikeQuery(rawQuery) && barcodes.includes(rawQuery)) rank = 1;
      else if (this.isSkuLikeQuery(rawQuery) && normalizedSkus.includes(normalizedQuery)) rank = 2;
      else if (/^\d{8,12}$/.test(rawQuery) && barcodes.some(barcode => barcode.startsWith(rawQuery))) rank = 3;
      else if (lowerSkus.some(sku => sku.startsWith(rawQuery)) || (this.isSkuLikeQuery(rawQuery) && normalizedSkus.some(sku => sku.startsWith(normalizedQuery)))) rank = 4;
      else if (textQuery && title === textQuery) rank = 5;
      else if (textQuery && title.startsWith(textQuery)) rank = 6;
      else if (queryTokens.length && queryTokens.every(token => title.includes(token))) rank = 7;
      else if (textQuery && (productType === textQuery || productType.startsWith(textQuery))) rank = 8;
      else if (queryTokens.length && queryTokens.every(token => productType.includes(token))) rank = 9;
      else if (textQuery && tags.some(tag => tag === textQuery || tag.startsWith(textQuery) || queryTokens.every(token => tag.includes(token)))) rank = 10;
      return { item, index, rank };
    }).sort((a, b) => a.rank - b.rank || a.index - b.index);

    // Keep only exact SKU/EAN matches when they exist; otherwise retain suggestions.
    const exactMatches = ranked.filter(({ rank }) => rank <= 2);
    const barcodePrefixMatches = /^\d{8,12}$/.test(rawQuery)
      ? ranked.filter(({ rank }) => rank === 3) : [];
    const visibleMatches = exactMatches.length ? exactMatches
      : barcodePrefixMatches.length ? barcodePrefixMatches : ranked;
    if (exactMatches.length || barcodePrefixMatches.length) {
      const visibleItems = new Set(visibleMatches.map(({ item }) => item));
      ranked.filter(({ item }) => !visibleItems.has(item)).forEach(({ item }) => item.remove());
    }
    visibleMatches.forEach(({ item, rank }) => {
      item.classList.toggle('predictive-search__list-item--exact-sku', rank === 0 || rank === 2);
      item.classList.toggle('predictive-search__list-item--exact-ean', rank === 1);
      list.appendChild(item);
    });
    Array.from(list.children).slice(10).forEach(item => item.remove());
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');
    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(text) {
    if (!this.statusElement) return;
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = text;
    setTimeout(() => this.statusElement?.setAttribute('aria-hidden', 'true'), 1000);
  }

  renderSearchResults(markup) {
    this.predictiveSearchResults.innerHTML = markup;
    this.prioritizeSearchMatches();
    this.setAttribute('results', true);
    this.removeAttribute('loading');
    if (this.openExactBarcodeProduct()) return;
    this.open();
  }

  openExactBarcodeProduct() {
    const barcode = this.pendingExactBarcode;
    if (!barcode) return false;

    const matches = Array.from(this.predictiveSearchResults.querySelectorAll('#predictive-search-results-products-list .predictive-search__list-item'))
      .filter(item => (item.dataset.productBarcodes || '').split('|').map(value => value.trim()).includes(barcode));
    this.pendingExactBarcode = null;
    if (matches.length === 1) {
      const productLink = matches[0].querySelector('a[href*="/products/"]');
      if (productLink?.href) {
        window.location.assign(productLink.href);
        return true;
      }
    }

    // Scanner mode never shows suggestions when no exact barcode matches.
    this.predictiveSearchResults.innerHTML =
      '<div class="predictive-search__result-group">' +
      '<p role="status" class="predictive-search__heading text-body">' +
      'Aucun produit correspondant exactement au code-barres scanné.' +
      '</p></div>';
    this.open();
    return true;
  }

  getResultsMaxHeight() { return window.innerHeight - document.querySelector('.section-header').getBoundingClientRect().bottom; }

  open() {
    this.predictiveSearchResults.style.maxHeight = `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clear = false) { this.closeResults(clear); this.isOpen = false; }

  closeResults(clear = false) {
    if (clear) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    this.querySelector('[aria-selected="true"]')?.setAttribute('aria-selected', false);
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.predictiveSearchResults.removeAttribute('style');
  }
}

class PlanetLineBarcodeScanner {
  static installStyles() {
    if (document.getElementById('planet-line-barcode-scanner-styles')) return;
    const style = document.createElement('style');
    style.id = 'planet-line-barcode-scanner-styles';
    style.textContent = `
      .barcode-scan__button{display:none;align-items:center;justify-content:center;right:calc(var(--inputs-border-width) + 8.8rem);top:var(--inputs-border-width);background:transparent;color:rgb(var(--color-foreground));border:0;cursor:pointer;z-index:4}
      .barcode-scan__button svg{width:2.5rem;height:2.5rem;fill:none;stroke:currentColor;stroke-width:1.8;stroke-linecap:round;stroke-linejoin:round}
      .barcode-scan__button:hover svg{transform:scale(1.07)}
      .barcode-scanner[hidden]{display:none!important}
      .barcode-scanner{position:fixed;inset:0;z-index:20000;display:flex;align-items:center;justify-content:center;padding:1.6rem;background:rgba(0,0,0,.78)}
      .barcode-scanner__dialog{position:relative;width:min(100%,48rem);max-height:calc(100dvh - 3.2rem);overflow:auto;border-radius:1.2rem;background:rgb(var(--color-background));color:rgb(var(--color-foreground));box-shadow:0 2rem 6rem rgba(0,0,0,.35)}
      .barcode-scanner__header{display:flex;align-items:center;justify-content:space-between;padding:1.6rem 1.8rem 1rem}
      .barcode-scanner__title{margin:0;font-size:1.8rem;font-weight:700}
      .barcode-scanner__close{display:flex;align-items:center;justify-content:center;width:4rem;height:4rem;padding:0;border:0;background:transparent;color:inherit;cursor:pointer}
      .barcode-scanner__close svg{width:2rem;height:2rem;stroke:currentColor;stroke-width:2}
      .barcode-scanner__viewport{position:relative;margin:0 1.6rem;overflow:hidden;border-radius:.8rem;background:#111;aspect-ratio:4/3}
      .barcode-scanner__video{width:100%;height:100%;object-fit:cover}
      .barcode-scanner__target{position:absolute;left:8%;right:8%;top:50%;height:34%;transform:translateY(-50%);border:2px solid #fff;border-radius:.8rem;box-shadow:0 0 0 999px rgba(0,0,0,.18);pointer-events:none}
      .barcode-scanner__target:after{content:'';position:absolute;left:8%;right:8%;top:50%;height:2px;background:#e30613;box-shadow:0 0 8px rgba(227,6,19,.8)}
      .barcode-scanner__detected[hidden]{display:none!important}
      .barcode-scanner__detected{position:absolute;left:8%;right:8%;top:50%;z-index:2;transform:translateY(-50%);padding:1.4rem 1rem;border-radius:.8rem;background:rgba(8,120,48,.94);color:#fff;text-align:center;font-size:1.45rem;font-weight:700;line-height:1.45;box-shadow:0 .8rem 2.4rem rgba(0,0,0,.3)}
      .barcode-scanner__status{min-height:4.8rem;margin:0;padding:1.4rem 1.8rem 1.8rem;text-align:center;font-size:1.35rem;line-height:1.45}
      .barcode-scanner__status[aria-invalid='true']{color:#b00020;font-weight:600}
      .barcode-scanner__status--detected{color:#087830;font-weight:700}
      .barcode-scanner__formats{margin:-.8rem 1.8rem 1.6rem;text-align:center;color:rgba(var(--color-foreground),.68);font-size:1.15rem;line-height:1.4}
      body.barcode-scanner-open{overflow:hidden!important}
      @media(max-width:767px){.barcode-scan__button{display:flex}.search__input.field__input{padding-right:14.2rem}}
    `;
    document.head.appendChild(style);
  }

  static ensureModal() {
    if (this.modal) return;
    this.modal = document.createElement('div');
    this.modal.className = 'barcode-scanner';
    this.modal.hidden = true;
    this.modal.innerHTML = `
      <div class="barcode-scanner__dialog" role="dialog" aria-modal="true" aria-labelledby="barcode-scanner-title">
        <div class="barcode-scanner__header">
          <h2 class="barcode-scanner__title" id="barcode-scanner-title">Scanner un code-barres</h2>
          <button type="button" class="barcode-scanner__close" aria-label="Fermer le lecteur">
            <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 5l14 14M19 5L5 19"/></svg>
          </button>
        </div>
        <div class="barcode-scanner__viewport">
          <video class="barcode-scanner__video" playsinline muted></video>
          <div class="barcode-scanner__target" aria-hidden="true"></div>
          <div class="barcode-scanner__detected" role="status" hidden></div>
        </div>
        <p class="barcode-scanner__status" role="status" aria-live="polite">Placez le code-barres dans le cadre.</p>
        <p class="barcode-scanner__formats">Zoom automatique · Double vérification · EAN-13 · EAN-8 · UPC · Code 128</p>
      </div>`;
    document.body.appendChild(this.modal);
    this.video = this.modal.querySelector('.barcode-scanner__video');
    this.status = this.modal.querySelector('.barcode-scanner__status');
    this.detectedNotice = this.modal.querySelector('.barcode-scanner__detected');
    this.closeButton = this.modal.querySelector('.barcode-scanner__close');
    this.closeButton.addEventListener('click', () => this.close());
    this.modal.addEventListener('click', event => { if (event.target === this.modal) this.close(); });
    document.addEventListener('keydown', event => { if (event.key === 'Escape' && !this.modal.hidden) this.close(); });
  }

  static getLibraryUrl() {
    return 'https://cdn.jsdelivr.net/npm/@zxing/browser@0.2.1/umd/zxing-browser.min.js';
  }

  static loadLibrary() {
    if (window.ZXingBrowser) return Promise.resolve(window.ZXingBrowser);
    if (this.libraryPromise) return this.libraryPromise;
    this.libraryPromise = new Promise((resolve, reject) => {
      const source = this.getLibraryUrl();
      if (!source) {
        reject(new Error('Adresse du lecteur introuvable'));
        return;
      }
      const script = document.createElement('script');
      script.src = source;
      script.async = true;
      script.onload = () => window.ZXingBrowser ? resolve(window.ZXingBrowser) : reject(new Error('Lecteur indisponible'));
      script.onerror = () => reject(new Error('Chargement du lecteur impossible'));
      document.head.appendChild(script);
    });
    return this.libraryPromise;
  }

  static setStatus(message, isError = false, isDetected = false) {
    if (!this.status) return;
    this.status.textContent = message;
    this.status.setAttribute('aria-invalid', isError ? 'true' : 'false');
    this.status.classList.toggle('barcode-scanner__status--detected', isDetected);
  }

  static async open(input) {
    this.installStyles();
    this.ensureModal();
    this.activeInput = input;
    this.detected = false;
    this.candidateValue = '';
    this.candidateCount = 0;
    this.candidateTimestamp = 0;
    this.detectedNotice.hidden = true;
    this.detectedNotice.textContent = '';
    this.modal.hidden = false;
    document.body.classList.add('barcode-scanner-open');
    this.setStatus('Initialisation de la caméra…');
    this.closeButton.focus();

    if (!window.isSecureContext || !navigator.mediaDevices?.getUserMedia) {
      this.setStatus('La caméra n’est pas disponible dans ce navigateur.', true);
      return;
    }

    try {
      const ZXing = await this.loadLibrary();
      if (this.modal.hidden) return;
      const formats = [
        ZXing.BarcodeFormat.EAN_13,
        ZXing.BarcodeFormat.EAN_8,
        ZXing.BarcodeFormat.UPC_A,
        ZXing.BarcodeFormat.UPC_E,
        ZXing.BarcodeFormat.CODE_128
      ];
      const hints = new Map();
      hints.set(2, formats); // DecodeHintType.POSSIBLE_FORMATS
      hints.set(3, true); // DecodeHintType.TRY_HARDER
      this.reader = new ZXing.BrowserMultiFormatReader(hints, {
        delayBetweenScanAttempts: 150,
        delayBetweenScanSuccess: 220
      });
      this.setStatus('Placez le code-barres dans le cadre.');
      this.controls = await this.reader.decodeFromConstraints(
        {
          audio: false,
          video: {
            facingMode: { ideal: 'environment' },
            width: { ideal: 1920 },
            height: { ideal: 1080 },
            frameRate: { ideal: 30, min: 24 },
            advanced: [{ zoom: 1.35 }]
          }
        },
        this.video,
        result => {
          if (!result || this.detected) return;
          const value = typeof result.getText === 'function' ? result.getText() : result.text;
          if (!value) return;
          const normalizedValue = String(value).trim();
          const now = Date.now();
          if (normalizedValue === this.candidateValue && now - this.candidateTimestamp <= 1400) {
            this.candidateCount += 1;
          } else {
            this.candidateValue = normalizedValue;
            this.candidateCount = 1;
          }
          this.candidateTimestamp = now;
          if (this.candidateCount < 2) {
            this.setStatus(`Code repéré : ${normalizedValue} — confirmation…`);
            return;
          }
          this.detected = true;
          this.showDetectedResult(normalizedValue);
        }
      );
      await this.configureCamera();
    } catch (error) {
      const denied = error?.name === 'NotAllowedError' || error?.name === 'SecurityError';
      const missing = error?.name === 'NotFoundError' || error?.name === 'DevicesNotFoundError';
      if (denied) this.setStatus('Autorisez l’accès à la caméra pour scanner le code-barres.', true);
      else if (missing) this.setStatus('Aucune caméra compatible n’a été détectée.', true);
      else this.setStatus('Le lecteur n’a pas pu démarrer. Vous pouvez saisir l’EAN manuellement.', true);
    }
  }

  static async configureCamera() {
    const track = this.video?.srcObject?.getVideoTracks?.()[0];
    if (!track?.getCapabilities || !track?.applyConstraints) return;

    const capabilities = track.getCapabilities();
    const focusModes = Array.isArray(capabilities.focusMode) ? capabilities.focusMode : [];
    if (focusModes.includes('continuous')) {
      try { await track.applyConstraints({ advanced: [{ focusMode: 'continuous' }] }); }
      catch (error) { /* L'appareil conserve alors son autofocus natif. */ }
    }

    const zoom = capabilities.zoom;
    if (!zoom || !Number.isFinite(zoom.min) || !Number.isFinite(zoom.max) || zoom.max <= zoom.min) return;
    const currentZoom = Number(track.getSettings?.().zoom) || zoom.min;
    const targetZoom = Math.min(zoom.max, Math.max(zoom.min, currentZoom, 1.35));
    if (targetZoom <= currentZoom) return;
    try { await track.applyConstraints({ advanced: [{ zoom: targetZoom }] }); }
    catch (error) { /* Le scan continue sans zoom si la commande est refusée. */ }
  }

  static showDetectedResult(value) {
    try { this.controls?.stop(); } catch (error) { /* Le flux peut déjà être arrêté. */ }
    this.controls = null;
    const stream = this.video?.srcObject;
    if (stream?.getTracks) stream.getTracks().forEach(track => track.stop());
    this.detectedNotice.textContent = `Code détecté : ${value}`;
    this.detectedNotice.hidden = false;
    this.setStatus(`Code détecté : ${value} — recherche en cours…`, false, true);
    this.pendingResultTimer = setTimeout(() => {
      this.pendingResultTimer = null;
      this.applyResult(value);
    }, 1300);
  }

  static applyResult(value) {
    const input = this.activeInput;
    this.close();
    if (!input) return;
    input.value = value;
    input.focus();

    const predictiveSearch = input.closest('predictive-search');
    if (predictiveSearch?.onChange) {
      predictiveSearch.pendingExactBarcode = value;
      predictiveSearch.searchTerm = '';
      predictiveSearch.onChange();
    } else input.dispatchEvent(new Event('input', { bubbles: true }));
  }

  static close() {
    if (this.pendingResultTimer) clearTimeout(this.pendingResultTimer);
    this.pendingResultTimer = null;
    try { this.controls?.stop(); } catch (error) { /* La caméra est peut-être déjà arrêtée. */ }
    this.controls = null;
    const stream = this.video?.srcObject;
    if (stream?.getTracks) stream.getTracks().forEach(track => track.stop());
    if (this.video) {
      this.video.pause();
      this.video.srcObject = null;
    }
    if (this.detectedNotice) {
      this.detectedNotice.hidden = true;
      this.detectedNotice.textContent = '';
    }
    if (this.modal) this.modal.hidden = true;
    document.body.classList.remove('barcode-scanner-open');
  }
}

customElements.define('predictive-search', PredictiveSearch);
